import { makeStyles } from "@mui/styles";
import React from "react";
import {
  ACTIONS,
  BASE,
  BORDER,
  BRAND,
  GREY,
  PRIMARY,
} from "../../utils/constants/colors";
import { fontSize, fontWeight, lineHeight } from "../../utils/constants/UI";
import IconComponent from "../Icon/IconComponent";
import CircularLoader from "../Loaders/CircularLoader";

const SIZE_MAPPING = {
  xs34: {
    fontSize: fontSize.XS,
    fontWeight: fontWeight.MEDIUM,
    lineHeight: lineHeight.XS,
    width: "16px",
    iconSize: "16px",
    height: "16px",
    padding: "8px 14px",
  },
  sm36: {
    fontSize: fontSize.SM,
    fontWeight: fontWeight.MEDIUM,
    lineHeight: lineHeight.SM,
    width: "16px",
    iconSize: "16px",
    height: "16px",
    padding: "8px 14px",
  },
  md40: {
    fontSize: fontSize.SM,
    fontWeight: fontWeight.MEDIUM,
    lineHeight: lineHeight.SM,
    width: "20px",
    iconSize: "20px",
    height: "20px",
    padding: "10px 16px",
  },
  lg44: {
    fontSize: fontSize.MD,
    fontWeight: fontWeight.MEDIUM,
    lineHeight: lineHeight.MD,
    width: "20px",
    iconSize: "20px",
    height: "20px",
    padding: "10px 16px",
  },
  primary: {
    color: BRAND.PRIMARY,
  },
  ghost: {
    color: BRAND.PRIMARY,
  },
  text: {
    color: GREY.SECONDARY,
  },
};

export const styles = makeStyles({
  primary: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    backgroundColor: (props) =>
      props.isLoading
        ? PRIMARY.W_400
        : props.disabled
        ? BORDER.DISABLED
        : BRAND.PRIMARY,
    color: (props) => (props.disabled ? GREY.DISABLED : BASE.WHITE),
    gap: "4px",
    padding: (props) => SIZE_MAPPING[props?.size]?.padding,
    borderRadius: "999px", // DESIGN-SYSTEM pill buttons
    border: "none",
    whiteSpace: "nowrap",
    cursor: (props) => (!props.disabled ? "pointer" : "not-allowed"),
    fontSize: (props) => SIZE_MAPPING[props.size].fontSize,
    fontWeight: 600, // DESIGN-SYSTEM pill button weight
    lineHeight: (props) => SIZE_MAPPING[props.size].lineHeight,
    "&:hover": {
      backgroundColor: (props) => !props.disabled && PRIMARY.W_600,
    },
    "&:active": {
      backgroundColor: (props) =>
        !props.isLoading && !props.disabled && ACTIONS.DARK,
    },
  },
  ghost: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    backgroundColor: (props) =>
      props.isLoading
        ? PRIMARY.W_400
        : props.disabled
        ? BORDER.DISABLED
        : BASE.WHITE,
    color: (props) =>
      props.isLoading
        ? BASE.WHITE
        : props.disabled
        ? GREY.DISABLED
        : GREY.SECONDARY,
    gap: "4px",
    padding: (props) => SIZE_MAPPING[props?.size]?.padding,
    borderRadius: "999px", // DESIGN-SYSTEM pill buttons
    border: "none",
    whiteSpace: "nowrap",
    boxShadow: (props) =>
      !props.disabled &&
      `inset -1px 0 0 ${BORDER.DARK}, inset 0 -1px 0 ${BORDER.DARK}, inset 1px 0 0 ${BORDER.DARK}, inset 0 1px 0 ${BORDER.DARK}`,
    cursor: (props) => (!props.disabled ? "pointer" : "not-allowed"),
    fontSize: (props) => SIZE_MAPPING[props.size].fontSize,
    fontWeight: 600, // DESIGN-SYSTEM pill button weight
    lineHeight: (props) => SIZE_MAPPING[props.size].lineHeight,
    "&:hover": {
      backgroundColor: (props) => !props.disabled && ACTIONS.DEFAULT,
      borderColor: (props) => !props.disabled && BORDER.DEFAULT,
      color: (props) => !props.disabled && GREY.SECONDARY,
    },
    "&:active": {
      backgroundColor: (props) =>
        !props.isLoading && !props.disabled && PRIMARY.W_50,
      boxShadow: (props) =>
        !props.isLoading &&
        !props.disabled &&
        `inset -1px 0 0 ${BRAND.PRIMARY}, inset 0 -1px 0 ${BRAND.PRIMARY}, inset 1px 0 0 ${BRAND.PRIMARY}, inset 0 1px 0 ${BRAND.PRIMARY}`,
      color: (props) => !props.isLoading && !props.disabled && BRAND.PRIMARY,
      "& .MuiIcon-root": {
        color: (props) => !props.isLoading && !props.disabled && BRAND.PRIMARY,
      },
    },
  },
  text: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    backgroundColor: "transparent",
    color: (props) => (!props.disabled ? GREY.SECONDARY : GREY.DISABLED),
    gap: "4px",
    padding: (props) => SIZE_MAPPING[props?.size]?.padding,
    borderRadius: "999px", // DESIGN-SYSTEM pill buttons
    border: "none",
    whiteSpace: "nowrap",
    cursor: (props) => (!props.disabled ? "pointer" : "not-allowed"),
    fontSize: (props) => SIZE_MAPPING[props.size].fontSize,
    fontWeight: 600, // DESIGN-SYSTEM pill button weight
    lineHeight: (props) => SIZE_MAPPING[props.size].lineHeight,
    "&:active": {
      color: (props) => !props.isLoading && !props.disabled && BRAND.PRIMARY,
      "& .MuiCircularProgress-root": {
        color: (props) => !props.isLoading && !props.disabled && BRAND.PRIMARY,
      },
      "& .MuiIcon-root": {
        color: (props) => !props.isLoading && !props.disabled && BRAND.PRIMARY,
      },
    },
    "&:hover": {
      color: (props) => !props.disabled && GREY.PRIMARY,
      "& .MuiIcon-root": {
        color: (props) => !props.isLoading && !props.disabled && GREY.PRIMARY,
      },
    },
  },
  iconStyle: {
    fontSize: (props) => `${SIZE_MAPPING[props.size].iconSize} !important`,
    color: (props) =>
      (props.variant === "ghost" || props?.variant === "text") &&
      (props.disabled ? GREY.DISABLED : GREY.TERTIARY),
  },
  loaderIcon: {
    width: (props) => `${SIZE_MAPPING[props.size].width} !important`,
    height: (props) => `${SIZE_MAPPING[props.size].height} !important`,
    margin: "0px !important",
  },
});

const Button = ({
  label,
  LeftIcon,
  RightIcon,
  isLoading,
  size,
  variant,
  disabled,
  ...props
}) => {
  const classes = styles({ size, variant, disabled, isLoading });
  return (
    <button className={classes[variant]} {...props}>
      {LeftIcon ? (
        isLoading ? (
          <CircularLoader
            className={classes.loaderIcon}
            loadercolor={SIZE_MAPPING[variant]?.color}
          />
        ) : (
          <IconComponent iconLabel={LeftIcon} className={classes.iconStyle} />
        )
      ) : null}
      {!LeftIcon && !RightIcon && isLoading && (
        <CircularLoader
          loadercolor={SIZE_MAPPING[variant]?.color}
          className={classes.loaderIcon}
        />
      )}
      {label}
      {RightIcon ? (
        !LeftIcon && isLoading ? (
          <CircularLoader
            loadercolor={SIZE_MAPPING[variant]?.color}
            className={classes.loaderIcon}
          />
        ) : (
          <IconComponent iconLabel={RightIcon} className={classes.iconStyle} />
        )
      ) : null}
    </button>
  );
};

export default Button;

// ------------- NOTE ----------------
// If there is need to add color or any other attribute in button then please use SIZE_MAPPING object.
// Add key into object and use it with props according to size or variant.
// Props description :-
// label : Label you need to display on Button.
// LeftIcon : Icon displayed on left of the label.
// RightIcon : Icon displayed on right of the label.
// isLoading : this prop will enable loader in button.
// size : this prop is for the size of button.
// variant : this prop is used for the variant(primary, ghost, text) of button.
// disabled : this prop will have value true/false.
